import { deriveTaskDisplayStatus, type TaskStatus } from '@voll-intelligence/domain';

import type { TaskWithCard } from '@web/data/repositories';
import { t } from '@web/i18n';
import { AppIcons, Card, TaskStatusIcon, Tooltip } from '@web/ui/core/atoms';
import { formatHhMm, twcn } from '@web/ui/core/utils';

const STATE_LABEL: Partial<Record<TaskStatus, Parameters<typeof t>[0]>> = {
  running: 'workspace.taskState.running',
  pending: 'workspace.taskState.pending',
  blocked: 'workspace.taskState.blocked',
  done: 'workspace.taskState.done',
  failed: 'workspace.taskState.failed',
};

const STATE_TONE: Partial<Record<TaskStatus, 'brand' | 'amber' | 'gray' | 'green' | 'red'>> = {
  running: 'brand',
  pending: 'gray',
  blocked: 'amber',
  done: 'green',
  failed: 'red',
};

const STAT_TONE_CLASS: Record<'brand' | 'amber' | 'gray' | 'green' | 'red', string> = {
  brand: 'bg-primary-tint text-primary border-primary/22',
  amber: 'bg-tone-amber-tint text-tone-amber-fg border-tone-amber-fg/22',
  gray: 'bg-muted text-muted-foreground border-border-soft',
  green: 'bg-tone-green-tint text-tone-green-fg border-tone-green-fg/22',
  red: 'bg-tone-red-tint text-tone-red-fg border-tone-red-fg/22',
};

export interface TaskCardProps {
  task: TaskWithCard;
  onClick: () => void;
}

/**
 * One task rendered as a vertical card. Shared by the "Minhas tarefas"
 * hero and the tasks kanban board. Status box tone + state label come
 * from the raw status; the muted detail strip surfaces block/error
 * context via `renderDetail`.
 */
export function TaskCard({ task, onClick }: Readonly<TaskCardProps>): React.JSX.Element {
  const stateLabelKey = STATE_LABEL[task.status];
  const tone = STATE_TONE[task.status] ?? 'gray';
  const detail = renderDetail(task);
  const updatedAt = formatHhMm(task.updatedAt);
  const customerFirstName = task.card.customer.name.split(/\s+/)[0] ?? '';

  return (
    <Card onTap={onClick} aria-label={task.description}>
      <div className="flex min-h-task-card flex-col gap-2 p-3.5">
        <div className="flex items-center gap-2 text-card-id text-muted-foreground">
          <span
            aria-hidden
            className={twcn(
              'inline-flex h-6.5 w-6.5 flex-shrink-0 items-center justify-center rounded-md border',
              STAT_TONE_CLASS[tone],
            )}
          >
            <TaskStatusIcon status={deriveTaskDisplayStatus(task)} size={14} />
          </span>
          {stateLabelKey ? (
            <span className="font-mono text-tiny font-medium uppercase tracking-eyebrow text-foreground">
              {t(stateLabelKey)}
            </span>
          ) : null}
          <span className="flex-1" />
          <span className="font-mono text-card-id text-faint-foreground tabular-nums">
            {updatedAt}
          </span>
        </div>
        <Tooltip label={task.description}>
          <span className="line-clamp-1 text-card-body font-medium leading-card-body tracking-display-soft text-foreground">
            {task.description}
          </span>
        </Tooltip>
        {detail ? (
          <span className="rounded-sm bg-muted px-2.5 py-1.5 text-micro leading-card-meta text-muted-foreground">
            {detail}
          </span>
        ) : null}
        <div className="mt-0.5 flex items-center gap-1.5 text-meta text-muted-foreground">
          <AppIcons name="link-simple" size={12} className="text-faint-foreground" />
          <span className="font-mono text-card-id">#{task.card.seq}</span>
          <span className="text-faint-foreground">·</span>
          <span className="truncate font-medium text-foreground">{customerFirstName}</span>
          <span className="flex-1" />
          <span className="inline-flex items-center gap-1 text-micro font-medium text-primary">
            <span>{t('workspace.myTasks.open')}</span>
            <AppIcons name="arrow-right" size={12} />
          </span>
        </div>
      </div>
    </Card>
  );
}

function renderDetail(task: TaskWithCard): string | null {
  if (task.status === 'blocked' && task.blockReason) {
    const br = task.blockReason;
    if (br.kind === 'human_in_the_loop')
      return t('board.drawer.tasks.block.hitl', { gate: br.gate });
    if (br.kind === 'awaiting_input') {
      return br.source === 'client'
        ? t('workspace.block.awaitingClient')
        : t('workspace.block.awaitingVendor');
    }
    return t('workspace.myTasks.detail.scheduled', {
      datetime: new Date(br.runAt).toLocaleString(undefined),
    });
  }
  if (task.status === 'failed' && task.errorCode)
    return t('board.drawer.tasks.errorCode', { code: task.errorCode });
  return null;
}
